import React, { useState } from 'react';
import { motion } from 'framer-motion';
import DatePicker from 'react-datepicker';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useInvoice } from '../context/InvoiceContext';
import CustomerSelector from './CustomerSelector';
import 'react-datepicker/dist/react-datepicker.css';

const { FiPlus, FiTrash2, FiSave, FiDollarSign, FiHash, FiBriefcase } = FiIcons;

const MarketingInvoiceForm = () => {
  const { addInvoice, settings, saveCustomer } = useInvoice();
  const [formData, setFormData] = useState({
    customer: { name: '', address: '', phone: '', email: '' },
    items: [{ name: '', description: '', quantity: 1, price: 0, total: 0 }],
    paymentDate: new Date(),
    currency: 'USD',
    processingFee: 0,
    notes: '',
    invoiceNumber: settings.invoiceNumber,
    invoiceType: 'marketing'
  });

  const currencies = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' }
  ];

  const getCurrencySymbol = (code) => currencies.find(c => c.code === code)?.symbol || '$';

  const handleCustomerChange = (field, value) => {
    setFormData(prev => ({ ...prev, customer: { ...prev.customer, [field]: value } }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'price') {
      newItems[index].total = (newItems[index].quantity || 0) * (newItems[index].price || 0);
    }
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { name: '', description: '', quantity: 1, price: 0, total: 0 }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
    }
  };

  const calculateSubtotal = () => formData.items.reduce((sum, item) => sum + item.total, 0);
  const calculateTotal = () => calculateSubtotal() + (formData.processingFee || 0);

  const handleSubmit = (e) => {
    e.preventDefault();
    saveCustomer(formData.customer);

    const subtotal = calculateSubtotal();
    const total = calculateTotal();

    const invoice = {
      ...formData,
      subtotal,
      total,
      tax: 0, // No tax for this simple form
      companyInfo: { ...settings.companyInfo, logoUrl: settings.logoUrl },
      colors: settings.colors
    };

    addInvoice(invoice);

    setFormData({
      customer: { name: '', address: '', phone: '', email: '' },
      items: [{ name: '', description: '', quantity: 1, price: 0, total: 0 }],
      paymentDate: new Date(),
      currency: 'USD',
      processingFee: 0,
      notes: '',
      invoiceNumber: settings.invoiceNumber + 1,
      invoiceType: 'marketing'
    });

    alert('Marketing invoice created successfully!');
  };

  const subtotal = calculateSubtotal();
  const total = calculateTotal();

  return (
    <div className="max-w-4xl mx-auto p-6">
      <motion.form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-lg overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="p-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="bg-gray-100 p-3 rounded-lg">
              <SafeIcon icon={FiBriefcase} className="w-6 h-6 text-gray-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Marketing Service Invoice</h2>
              <p className="text-gray-500">A simple invoice for your marketing services.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Invoice Details</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Invoice Number</label>
                  <div className="relative">
                    <SafeIcon icon={FiHash} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={formData.invoiceNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Payment Date</label>
                  <DatePicker
                    selected={formData.paymentDate}
                    onChange={(date) => setFormData(prev => ({ ...prev, paymentDate: date }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    dateFormat="yyyy-MM-dd"
                  />
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Customer</h3>
              <CustomerSelector customer={formData.customer} onChange={handleCustomerChange} />
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Services</h3>
            <div className="space-y-4">
              {formData.items.map((item, index) => (
                <motion.div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200" layout>
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-medium text-gray-800">Service {index + 1}</h4>
                    {formData.items.length > 1 && (
                      <button type="button" onClick={() => removeItem(index)} className="text-red-500 p-1 rounded-full hover:bg-red-100">
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-gray-600 mb-1">Service Name</label>
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="e.g., SEO Audit"
                        required
                      />
                    </div>
                    <div className="lg:col-span-3">
                      <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Details of the service provided"
                      />
                    </div>
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-gray-600 mb-1">Currency</label>
                      <select
                        value={formData.currency}
                        onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        {currencies.map(c => <option key={c.code} value={c.code}>{c.code} - {c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Quantity</label>
                      <input
                        type="number"
                        min="0"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Price</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.price}
                        onChange={(e) => handleItemChange(index, 'price', Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Total</label>
                      <div className="px-3 py-2 bg-gray-200 rounded-lg font-medium text-gray-800">
                        {getCurrencySymbol(formData.currency)}{item.total.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <motion.button
              type="button"
              onClick={addItem}
              className="flex items-center gap-2 mt-4 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              Add Service
            </motion.button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Notes & Fees</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Notes (Optional)</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Payment terms, thank you note, etc."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Processing Fee (Optional)</label>
                  <div className="relative">
                    <SafeIcon icon={FiDollarSign} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.processingFee}
                      onChange={(e) => setFormData(prev => ({ ...prev, processingFee: Number(e.target.value) || 0 }))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal:</span>
                  <span className="font-medium text-gray-800">{getCurrencySymbol(formData.currency)}{subtotal.toFixed(2)}</span>
                </div>
                {formData.processingFee > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Processing Fee:</span>
                    <span className="font-medium text-gray-800">{getCurrencySymbol(formData.currency)}{formData.processingFee.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <div className="flex justify-between text-xl font-bold text-gray-800">
                    <span>Total:</span>
                    <span>{getCurrencySymbol(formData.currency)}{total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <motion.button
            type="submit"
            className="w-full bg-gray-800 text-white py-3 px-6 rounded-lg font-semibold text-lg hover:bg-gray-900 transition-colors flex items-center justify-center"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <SafeIcon icon={FiSave} className="w-5 h-5 mr-3" />
            Create & Save Invoice
          </motion.button>
        </div>
      </motion.form>
    </div>
  );
};

export default MarketingInvoiceForm;